import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_THEME_ID, THEME_STORAGE_KEY, THEMES, isKnownTheme } from './themeRegistry';
import { buildMuiTheme } from '../theme';

/**
 * Tema activo de la app. Aplica el tema como atributo data-theme en <html>
 * (las hojas de src/themes/ definen sus variables --wb-* para ese atributo)
 * y lo recuerda en este navegador. public/index.html aplica el guardado
 * antes de que React pinte, para que no haya un destello del tema anterior.
 */
const ThemeControllerContext = createContext({
  themeId: DEFAULT_THEME_ID,
  themes: THEMES,
  setThemeId: () => {},
  muiTheme: null
});

const readStoredTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored && isKnownTheme(stored) ? stored : DEFAULT_THEME_ID;
  } catch (error) {
    return DEFAULT_THEME_ID;
  }
};

export const applyThemeToDocument = (themeId) => {
  // Siempre con atributo: sin él, :root tiene los valores de oscuro.css
  // (hoja base), que no es el tema por defecto.
  document.documentElement.setAttribute('data-theme', themeId);
};

export const ThemeController = ({ children }) => {
  // El atributo se aplica ANTES de leer las variables para el tema de MUI
  // (buildMuiTheme las lee ya resueltas de <html>).
  const [themeId, setThemeIdState] = useState(() => {
    const initial = readStoredTheme();
    applyThemeToDocument(initial);
    return initial;
  });

  useEffect(() => {
    applyThemeToDocument(themeId);
  }, [themeId]);

  const setThemeId = useCallback((nextId) => {
    const id = isKnownTheme(nextId) ? nextId : DEFAULT_THEME_ID;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch (error) {
      // Sin almacenamiento (modo privado): el tema dura hasta recargar.
    }
    applyThemeToDocument(id);
    setThemeIdState(id);
  }, []);

  // Tema de MUI (diálogos, menús, alertas...) con los colores del tema activo.
  const muiTheme = useMemo(() => buildMuiTheme(), [themeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({ themeId, themes: THEMES, setThemeId, muiTheme }), [themeId, setThemeId, muiTheme]);
  return <ThemeControllerContext.Provider value={value}>{children}</ThemeControllerContext.Provider>;
};

export const useAppTheme = () => useContext(ThemeControllerContext);
